import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import InstallBanner from '../components/InstallBanner';
import { recordPortalVisit } from '../lib/installPrompt';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Upload, Check, X, Image as ImageIcon,
  AlertCircle, ArrowRight, Calendar, Lock, Heart,
  Mic, MicOff, Square, Play, Pause, MessageSquare,
  Tv, Radio, Home
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { MOODS, MoodIcon } from '../lib/moodIcons';
import formatEventDate from '../lib/formatEventDate';
import { useToast } from '../components/Toast';
import ReelEditor from '../components/ReelEditor';
import { Film } from 'lucide-react';
import { SkeletonGrid, EmptyState } from '../components/Skeleton';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PortalPage() {
  const { auraKey } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const ownedKey = auraKey ? `memouraa_owned_${auraKey}` : null;
  const [ownedTokens, setOwnedTokens] = useState({});
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  const [aura, setAura] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [guestName, setGuestName] = useState('');
  const [selectedMood, setSelectedMood] = useState(null);
  const [caption, setCaption] = useState('');
  const [uploads, setUploads] = useState([]);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [showMoodPrompt, setShowMoodPrompt] = useState(false);
  const [showCaptionPrompt, setShowCaptionPrompt] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [showReelEditor, setShowReelEditor] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [brand, setBrand] = useState(null);
  const [visitCount, setVisitCount] = useState(1);
  const cameraInputRef = useRef(null);
  
  // Text-note states (Sprint 37.6)
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [noteError, setNoteError] = useState('');

  // Voice toast states
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioPlayRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchAura();
    if (auraKey) setVisitCount(recordPortalVisit(auraKey));
    // Point manifest <link> to the per-aura manifest so install is branded
    const manifestLink = document.querySelector('link[rel="manifest"]');
    const previousHref = manifestLink?.getAttribute('href');
    if (manifestLink && auraKey) {
      manifestLink.setAttribute('href', `${API_URL}/api/portal/${auraKey}/manifest`);
    }
    return () => {
      if (manifestLink && previousHref) manifestLink.setAttribute('href', previousHref);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auraKey]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, [audioUrl]);

  const fetchAura = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/portal/${auraKey}`, { withCredentials: true });
      setAura(data);
      // `unlocked` is true when this browser already holds the password cookie.
      const open = !data.requires_password || data.unlocked || isUnlocked;
      setIsLocked(!open);
      if (data.unlocked) setIsUnlocked(true);

      if (open) {
        fetchMedia();
      }

      // Fetch brand settings in parallel — failure is non-fatal
      try {
        const brandRes = await axios.get(`${API_URL}/api/portal/${auraKey}/brand`);
        if (brandRes.data && brandRes.data.primary_color) {
          setBrand(brandRes.data);
        }
      } catch {
        // brand is optional — silently ignore
      }
    } catch (err) {
      setError('Aura not found');
    } finally {
      setLoading(false);
    }
  };

  // Guests list moments through the aura's key (never by numeric id). The
  // file URLs in the reply are signed and expire, so they are not shareable
  // beyond the event's guests.
  const fetchMedia = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/portal/${auraKey}/media`, { withCredentials: true });
      setMedia(data);
    } catch (err) {
      if (err.response?.status === 401) {
        setIsUnlocked(false);
        setIsLocked(true);
        return;
      }
      console.error('Error fetching media:', err);
    }
  };

  // Guest ownership: tokens returned at upload time, kept locally so a guest can
  // delete their own moments from this browser.
  useEffect(() => {
    if (!ownedKey) return;
    try { setOwnedTokens(JSON.parse(localStorage.getItem(ownedKey) || '{}')); }
    catch { setOwnedTokens({}); }
  }, [ownedKey]);

  const rememberOwned = (id, token) => {
    if (!ownedKey || !id || !token) return;
    setOwnedTokens(prev => {
      const next = { ...prev, [id]: token };
      try { localStorage.setItem(ownedKey, JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const guestDeleteMedia = async (id) => {
    const token = ownedTokens[id];
    if (!token) return;
    if (!window.confirm('Delete your moment? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/portal/media/${id}`, { data: { owner_token: token } });
      setMedia(ms => ms.filter(m => m.id !== id));
      setOwnedTokens(prev => {
        const next = { ...prev }; delete next[id];
        try { localStorage.setItem(ownedKey, JSON.stringify(next)); } catch {}
        return next;
      });
      toast.success('Your moment was deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete');
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    setPasswordError('');
    
    try {
      await axios.post(`${API_URL}/api/portal/${auraKey}/verify`, { password }, { withCredentials: true });
      setIsUnlocked(true);
      setIsLocked(false);
      fetchMedia();
    } catch (err) {
      setPasswordError('Invalid password');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setPendingFiles(files);
    
    if (!guestName) {
      setShowNamePrompt(true);
    } else {
      setShowMoodPrompt(true);
    }
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    setShowNamePrompt(false);
    setShowMoodPrompt(true);
  };

  const handleMoodSelect = (mood) => {
    setSelectedMood(mood);
    setShowMoodPrompt(false);
    setShowCaptionPrompt(true);
  };

  const handleCaptionSubmit = (e) => {
    e.preventDefault();
    setShowCaptionPrompt(false);
    processFiles(pendingFiles, selectedMood, caption);
    setPendingFiles([]);
    setCaption('');
  };

  const processFiles = async (files, mood, captionText) => {
    for (const file of files) {
      const uploadId = Date.now() + Math.random();
      const isVideo = file.type.startsWith('video/');
      const isAudio = file.type.startsWith('audio/');
      
      setUploads(prev => [...prev, {
        id: uploadId,
        file,
        progress: 0,
        status: 'uploading',
        preview: URL.createObjectURL(file),
        isVideo,
        isAudio
      }]);

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('aura_id', aura.id);
        formData.append('aura_key', auraKey);
        formData.append('mood', mood || '');
        formData.append('caption', captionText || '');
        formData.append('guest_name', guestName || 'Anonymous');

        // Credentials: a locked aura accepts uploads only with the portal's
        // unlock cookie (or the host's login), which a cross-origin API never
        // receives without them.
        const { data: uploaded } = await axios.post(`${API_URL}/api/media/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploads(prev => prev.map(u =>
              u.id === uploadId ? { ...u, progress } : u
            ));
          }
        });
        rememberOwned(uploaded?.id, uploaded?.owner_token);

        setUploads(prev => prev.map(u =>
          u.id === uploadId ? { ...u, status: 'success', progress: 100 } : u
        ));

        // Refresh media if not moderated
        if (aura.privacy_mode !== 'moderated') {
          fetchMedia();
        }

      } catch (err) {
        console.error('Upload error:', err);
        toast.error(err.response?.data?.detail || `Couldn't upload ${file.name || 'your file'}`);
        setUploads(prev => prev.map(u =>
          u.id === uploadId ? { ...u, status: 'error' } : u
        ));
      }
    }
  };

  // Voice Toast Recording Functions
  const startRecording = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error('Voice recording is not supported in this browser. Try Chrome, Safari, or Firefox.');
      return;
    }
    if (typeof MediaRecorder === 'undefined') {
      toast.error('Voice recording is not supported in this browser.');
      return;
    }
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      toast.error('Voice recording requires a secure (https) connection.');
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = err && err.name;
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        toast.error('Microphone access was denied. Please allow it in your browser settings and try again.');
      } else if (name === 'NotFoundError') {
        toast.error('No microphone was detected on this device.');
      } else {
        toast.error('Could not access the microphone: ' + (err && err.message ? err.message : 'unknown error'));
      }
      return;
    }
    // Pick the first supported mime type. Safari needs mp4; Chrome/Firefox prefer webm/opus.
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4',
      'audio/ogg;codecs=opus',
    ];
    let chosenType = '';
    if (window.MediaRecorder && MediaRecorder.isTypeSupported) {
      chosenType = candidates.find(c => MediaRecorder.isTypeSupported(c)) || '';
    }
    try {
      const opts = chosenType ? { mimeType: chosenType, audioBitsPerSecond: 32000 } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, opts);
    } catch (err) {
      try {
        mediaRecorderRef.current = new MediaRecorder(stream);
      } catch (err2) {
        stream.getTracks().forEach(t => t.stop());
        toast.error('Recording failed to start: ' + (err2 && err2.message ? err2.message : 'unsupported codec'));
        return;
      }
    }
    audioChunksRef.current = [];

    mediaRecorderRef.current.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
    };

    mediaRecorderRef.current.onerror = (e) => {
      // eslint-disable-next-line no-console
      console.error('MediaRecorder error', e);
      toast.error('Recording error. Please try again.');
    };

    mediaRecorderRef.current.onstop = () => {
      const blobType = mediaRecorderRef.current?.mimeType || chosenType || 'audio/webm';
      const blob = new Blob(audioChunksRef.current, { type: blobType });
      setAudioBlob(blob);
      setAudioUrl(URL.createObjectURL(blob));
      stream.getTracks().forEach(track => track.stop());
    };

    try {
      mediaRecorderRef.current.start();
    } catch (err) {
      stream.getTracks().forEach(t => t.stop());
      toast.error('Recording failed to start: ' + (err && err.message ? err.message : 'unknown error'));
      return;
    }
    setIsRecording(true);
    setRecordingTime(0);

    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= 10) {
          stopRecording();
          return 10;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const playRecording = () => {
    if (audioPlayRef.current) {
      if (isPlaying) {
        audioPlayRef.current.pause();
        setIsPlaying(false);
      } else {
        audioPlayRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const discardRecording = () => {
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setRecordingTime(0);
  };

  const submitNote = async () => {
    const text = noteText.trim();
    if (!text) { setNoteError('Please write something'); return; }
    if (text.length > 1000) { setNoteError('Max 1000 characters'); return; }
    setNoteSubmitting(true);
    setNoteError('');
    try {
      const { data: note } = await axios.post(`${API_URL}/api/portal/${auraKey}/notes`, {
        guest_name: guestName || 'Anonymous',
        text,
        mood: selectedMood || undefined,
      }, { withCredentials: true });
      rememberOwned(note?.id, note?.owner_token);
      setShowNoteModal(false);
      setNoteText('');
      fetchMedia();
    } catch (err) {
      setNoteError(err.response?.data?.detail || 'Failed to send');
    } finally {
      setNoteSubmitting(false);
    }
  };

  const uploadVoiceToast = () => {
    if (!audioBlob) return;
    const blobType = audioBlob.type || 'audio/webm';
    const ext = blobType.includes('mp4') ? 'm4a'
              : blobType.includes('ogg') ? 'ogg'
              : blobType.includes('wav') ? 'wav'
              : 'webm';
    const file = new File([audioBlob], `voice-toast-${Date.now()}.${ext}`, { type: blobType });
    setPendingFiles([file]);
    setShowVoiceRecorder(false);
    discardRecording();
    
    if (!guestName) {
      setShowNamePrompt(true);
    } else {
      setShowMoodPrompt(true);
    }
  };

  const handleReact = async (mediaId, reaction) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/media/${mediaId}/react?reaction=${reaction}`, {}, { withCredentials: true });
      setMedia(media.map(m => m.id === mediaId ? { ...m, hearts: data.hearts, glows: data.glows } : m));
    } catch (err) {
      console.error('Error reacting:', err);
    }
  };

  const removeUpload = (uploadId) => {
    setUploads(prev => prev.filter(u => u.id !== uploadId));
  };

  const formatDate = () => formatEventDate(aura);

  if (loading) {
    return (
      <div className="min-h-screen bg-midnight p-4 sm:p-6">
        <div className="max-w-4xl mx-auto">
          <SkeletonGrid count={6} className="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" />
        </div>
      </div>
    );
  }

  if (error || !aura) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-error/20 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-error" />
          </div>
          <h2 className="text-2xl font-serif font-medium text-text-light mb-2">Portal not found</h2>
          <p className="text-text-muted mb-6">This link may be invalid or expired.</p>
          <Link to="/" className="btn-champagne inline-flex items-center gap-2">
            Go to Memouraa
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  // Locked state - show password form
  if (isLocked) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="aura-card rounded-2xl p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-aura-purple/30 to-aura-gold/30 flex items-center justify-center mx-auto mb-6">
            <Lock className="w-8 h-8 text-champagne" />
          </div>
          <h1 className="text-2xl font-serif font-medium text-text-light mb-2">
            {aura.title}
          </h1>
          <p className="text-text-muted mb-6">
            This aura is protected. Enter the password to continue.
          </p>
          
          <form onSubmit={handleUnlock}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="aura-input mb-4 text-center"
              placeholder="Enter password"
              autoFocus
              data-testid="portal-password-input"
            />
            {passwordError && (
              <p className="text-error text-sm mb-4">{passwordError}</p>
            )}
            <button
              type="submit"
              className="w-full btn-champagne"
              data-testid="portal-unlock-btn"
            >
              Enter Portal
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Validate hex colors and only apply brand styles when valid
  const isValidHex = (c) => typeof c === 'string' && /^#[0-9A-Fa-f]{6}$/.test(c);
  const brandPrimary = isValidHex(brand?.primary_color) ? brand.primary_color : null;
  const brandAccent = isValidHex(brand?.accent_color) ? brand.accent_color : null;

  return (
    <div
      className="min-h-screen bg-midnight"
      style={brandPrimary ? { backgroundColor: brandPrimary } : {}}
    >
      <Helmet>
        <title>{(aura?.title || 'Aura Portal') + ' — Memouraa'}</title>
        <meta name="description" content={aura?.description || 'Capture the Aura'} />
        <meta property="og:title" content={aura?.title || 'Capture the Aura.'} />
        <meta property="og:description" content={aura?.description || 'Capture the Aura'} />
        <meta property="og:image" content={`${API_URL}/api/og/aura/${auraKey}.png`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${API_URL}/api/og/aura/${auraKey}.png`} />
      </Helmet>
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-aura-purple/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-aura-gold/10 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <div className="glass-dark border-b border-champagne/10 sticky top-0 z-40"
        style={brandAccent ? { borderBottomColor: brandAccent + '30' } : {}}
      >
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            {brand?.logo_url ? (
              <img
                src={brand.logo_url}
                alt={brand.company_name || 'Brand logo'}
                className="h-8 object-contain"
                onError={e => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <>
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <span className="text-lg font-serif font-medium text-text-light" style={brandAccent ? { color: brandAccent } : {}}>
                  {brand?.company_name || 'Memouraa'}
                </span>
              </>
            )}
          </Link>
          <nav className="flex items-center gap-1 sm:gap-2 text-sm">
            <Link to={`/auracast/${auraKey}`} className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-text-muted hover:text-champagne hover:bg-champagne/10 transition-colors" title="Live wall">
              <Radio className="w-4 h-4" /> Live
            </Link>
            <Link to={`/auracast/${auraKey}/projection`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-text-muted hover:text-champagne hover:bg-champagne/10 transition-colors" title="Slideshow (fullscreen)">
              <Tv className="w-4 h-4" /> <span className="hidden sm:inline">Slideshow</span>
            </Link>
            <Link to="/" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-text-muted hover:text-champagne hover:bg-champagne/10 transition-colors" title="Home">
              <Home className="w-4 h-4" /> <span className="hidden sm:inline">{t('common.home')}</span>
            </Link>
          </nav>
        </div>
      </div>

      {auraKey === 'demo-memouraa-aura' && (
        <div className="bg-gradient-to-r from-aura-purple/20 to-aura-gold/20 border-y border-champagne/30 px-4 py-3">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <div className="text-text-light text-center sm:text-left">
              <span className="text-champagne font-medium">This is a read-only demo.</span>
              {' '}Explore the gallery, AuraCast, and slideshow — then create your own.
            </div>
            <Link to="/register" className="bg-champagne text-midnight px-4 py-2 rounded-lg font-medium hover:bg-champagne-light whitespace-nowrap">
              Create your Aura →
            </Link>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-8 relative z-10">
        {/* Aura Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          {aura.cover_image && (
            <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden border-4 border-champagne/30 aura-glow">
              <img
                src={aura.cover_image}
                alt={aura.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <p className="text-champagne text-sm font-medium uppercase tracking-widest mb-2">
            {t('portal.welcomeTo')}
          </p>
          <h1 className="text-3xl sm:text-4xl font-serif font-medium text-text-light mb-2">
            {aura.title}
          </h1>
          <p className="text-text-muted flex items-center justify-center gap-2">
            <Calendar className="w-4 h-4" />
            {formatDate()}
          </p>
          {aura.description && (
            <p className="text-text-muted mt-4 max-w-lg mx-auto">
              {aura.description}
            </p>
          )}
          {aura.face_recognition_enabled && (
            <div className="mt-4 max-w-lg mx-auto" data-testid="find-me-notice">
              <a
                href={`/portal/${auraKey}/find-me`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-champagne/10 hover:bg-champagne/20 border border-champagne/30 text-champagne text-sm transition-colors"
              >
                <ImageIcon className="w-4 h-4" />
                Find your photos
              </a>
              <p className="text-text-muted text-xs mt-2">
                {aura.find_me_notice || 'Find me is on for this event: any guest can choose to search the photos for their own face with a selfie. Faces are only detected while someone runs a search, and no face data is stored.'}
              </p>
            </div>
          )}
        </motion.div>

        {/* Vaulted demo banner — read-only */}
        {aura?.is_vaulted && (
          <div className="mb-8 aura-card rounded-2xl p-6 border border-champagne/30 text-center" data-testid="vault-banner">
            <Lock className="w-8 h-8 text-champagne mx-auto mb-3" />
            <h3 className="text-xl font-serif text-champagne mb-2">This is a read-only demo</h3>
            <p className="text-text-muted text-sm max-w-xl mx-auto">
              The host has locked this Aura. You can view photos, moods, and the live wall,
              but new uploads, voice toasts, and notes are disabled. Hosts can still moderate,
              download archives, run AuraCast, and print seating charts.
            </p>
          </div>
        )}

        {/* Upload Area - hidden when vaulted */}
        {!aura?.is_vaulted && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 grid grid-cols-1 sm:grid-cols-3 gap-4"
        >
          {/* Hidden file inputs */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*,video/*"
            multiple
            className="hidden"
            data-testid="file-input"
          />
          <input
            type="file"
            ref={cameraInputRef}
            onChange={handleFileSelect}
            accept="image/*,video/*"
            capture="environment"
            className="hidden"
            data-testid="camera-input"
          />

          {/* Take Photo (native camera on mobile, file picker fallback on desktop) */}
          <button
            onClick={() => cameraInputRef.current?.click()}
            className="aura-card border-2 border-dashed border-champagne/30 rounded-2xl p-6 hover:border-champagne/60 transition-colors group"
            data-testid="camera-btn"
          >
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-aura-purple/30 to-aura-gold/30 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <ImageIcon className="w-7 h-7 text-champagne" />
              </div>
              <h3 className="text-lg font-serif font-medium text-text-light mb-1">
                {t('portal.takePhoto')}
              </h3>
              <p className="text-text-muted text-sm">
                {t('portal.takePhotoDesc')}
              </p>
            </div>
          </button>

          {/* Choose from library */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="aura-card border-2 border-dashed border-champagne/30 rounded-2xl p-6 hover:border-champagne/60 transition-colors group"
            data-testid="upload-btn"
          >
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-aura-purple/30 to-aura-gold/30 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Upload className="w-7 h-7 text-champagne" />
              </div>
              <h3 className="text-lg font-serif font-medium text-text-light mb-1">
                {t('portal.addYourLight')}
              </h3>
              <p className="text-text-muted text-sm">
                {t('portal.addYourLightDesc')}
              </p>
            </div>
          </button>

          {/* Leave a Note Button (S37.6) */}
          <button
            onClick={() => { setShowNoteModal(true); setNoteError(''); setNoteText(''); }}
            className="aura-card border-2 border-dashed border-champagne/30 rounded-2xl p-6 hover:border-champagne/60 transition-colors group"
            data-testid="leave-note-btn"
          >
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-aura-purple/30 to-aura-gold/30 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <MessageSquare className="w-7 h-7 text-champagne" />
              </div>
              <h3 className="text-lg font-serif font-medium text-text-light mb-1">{t('portal.leaveANote')}</h3>
              <p className="text-text-muted text-sm">{t('portal.leaveANoteDesc')}</p>
            </div>
          </button>

          {/* Voice Toast Button */}
          <button
            onClick={() => setShowVoiceRecorder(true)}
            className="aura-card border-2 border-dashed border-champagne/30 rounded-2xl p-6 hover:border-champagne/60 transition-colors group"
            data-testid="voice-toast-btn"
          >
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-aura-purple/30 to-aura-gold/30 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <Mic className="w-7 h-7 text-champagne" />
              </div>
              <h3 className="text-lg font-serif font-medium text-text-light mb-1">
                {t('portal.voiceToast')}
              </h3>
              <p className="text-text-muted text-sm">
                {t('portal.voiceToastDesc')}
              </p>
            </div>
          </button>

          {/* Make a Reel — only when at least 3 image media exist */}
          {media.filter(m => m.file_type === 'image').length >= 3 && (
            <button
              onClick={() => setShowReelEditor(true)}
              className="aura-card border-2 border-dashed border-champagne/30 rounded-2xl p-6 hover:border-champagne/60 transition-colors group"
              data-testid="make-reel-btn"
            >
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-aura-purple/30 to-aura-gold/30 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <Film className="w-7 h-7 text-champagne" />
                </div>
                <h3 className="text-lg font-serif font-medium text-text-light mb-1">Make a Reel</h3>
                <p className="text-text-muted text-sm">Mix photos into a 15s video</p>
              </div>
            </button>
          )}
        </motion.div>
        )}

        {/* Upload Progress */}
        <AnimatePresence>
          {uploads.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8"
            >
              <h3 className="text-lg font-serif font-medium text-text-light mb-4">Uploading</h3>
              <div className="space-y-3">
                {uploads.map((upload) => (
                  <div
                    key={upload.id}
                    className="aura-card rounded-xl p-4 flex items-center gap-4"
                  >
                    <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-midnight-light">
                      {upload.isVideo ? (
                        <video src={upload.preview} className="w-full h-full object-cover" />
                      ) : upload.isAudio ? (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-aura-purple/20 to-aura-gold/20">
                          <Mic className="w-7 h-7 text-champagne" />
                        </div>
                      ) : (
                        <img src={upload.preview} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-light truncate">{upload.file.name}</p>
                      {upload.status === 'uploading' && (
                        <div className="mt-2 h-2 bg-midnight-light rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-gradient-to-r from-aura-purple to-aura-gold"
                            initial={{ width: 0 }}
                            animate={{ width: `${upload.progress}%` }}
                          />
                        </div>
                      )}
                      {upload.status === 'success' && (
                        <p className="text-sm text-success flex items-center gap-1 mt-1">
                          <Check className="w-4 h-4" />
                          Moment Preserved
                        </p>
                      )}
                      {upload.status === 'error' && (
                        <p className="text-sm text-error flex items-center gap-1 mt-1">
                          <X className="w-4 h-4" />
                          Failed
                        </p>
                      )}
                    </div>
                    {upload.status !== 'uploading' && (
                      <button
                        onClick={() => removeUpload(upload.id)}
                        className="p-2 text-text-muted hover:text-error transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gallery */}
        {media.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-lg font-serif font-medium text-text-light mb-4">
              {t('portal.livingGallery')} ({media.length} moments)
            </h3>
            <div className="masonry-grid">
              {media.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03 }}
                  className="masonry-item rounded-xl overflow-hidden aura-card"
                >
                  {item.file_type === 'video' ? (
                    <video
                      src={`${API_URL}${item.url}`}
                      className="w-full aspect-square object-cover"
                      controls
                    />
                  ) : item.file_type === 'audio' ? (
                    <div className="w-full aspect-square flex flex-col items-center justify-center bg-gradient-to-br from-aura-purple/20 to-aura-gold/20 p-4">
                      <Mic className="w-12 h-12 mb-2 text-champagne" />
                      <p className="text-sm text-text-muted mb-2">Voice Toast</p>
                      <audio src={`${API_URL}${item.url}`} controls className="w-full" />
                    </div>
                  ) : item.file_type === 'text' ? (
                    <div className="w-full aspect-square flex flex-col items-center justify-center bg-gradient-to-br from-aura-purple/20 to-aura-gold/10 p-5">
                      <MessageSquare className="w-8 h-8 mb-3 text-champagne" />
                      <blockquote className="text-text-light text-sm italic text-center leading-relaxed line-clamp-6">
                        "{item.text_content}"
                      </blockquote>
                    </div>
                  ) : (
                    <div className="relative">
                      <img
                        src={`${API_URL}${item.url}`}
                        alt={item.caption || `Moment from ${item.guest_name || 'a guest'}`}
                        className="w-full aspect-square object-cover"
                      />
                      {item.mood && (
                        <span className="absolute top-2 left-2 inline-flex items-center gap-1 bg-midnight/80 backdrop-blur-sm border border-champagne/30 text-champagne text-xs px-2 py-1 rounded-full">
                          <MoodIcon mood={item.mood} className="w-3 h-3" />
                          {item.mood}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-text-light">{item.guest_name}</span>
                      {item.mood && <MoodIcon mood={item.mood} className="w-5 h-5 text-champagne" />}
                    </div>
                    {item.caption && (
                      <p className="text-xs text-text-muted mb-2 line-clamp-2">{item.caption}</p>
                    )}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleReact(item.id, 'heart')}
                        className="flex items-center gap-1 text-sm text-text-muted hover:text-error transition-colors"
                      >
                        <Heart className="w-4 h-4" /> {item.hearts}
                      </button>
                      <button
                        onClick={() => handleReact(item.id, 'glow')}
                        className="flex items-center gap-1 text-sm text-text-muted hover:text-champagne transition-colors"
                      >
                        <Sparkles className="w-4 h-4" /> {item.glows}
                      </button>
                      {ownedTokens[item.id] && (
                        <button
                          onClick={() => guestDeleteMedia(item.id)}
                          className="flex items-center gap-1 text-sm text-text-muted hover:text-error transition-colors ml-auto"
                          aria-label="Delete your moment"
                          data-testid="guest-delete-btn"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {media.length === 0 && (
          <EmptyState
            icon={ImageIcon}
            title="Be the first to share a moment"
            description="Upload a photo, video, or note to start this Aura's living gallery."
          />
        )}
      </div>

      {/* Name Prompt Modal */}
      <AnimatePresence>
        {showNamePrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="aura-card rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-xl font-serif font-medium text-text-light mb-2">
                What's your name?
              </h3>
              <p className="text-text-muted mb-4">
                This will be shown with your moments (optional)
              </p>
              <form onSubmit={handleNameSubmit}>
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Enter your name"
                  className="aura-input mb-4 text-center"
                  autoFocus
                  data-testid="guest-name-input"
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNamePrompt(false);
                      setGuestName('Anonymous');
                      setShowMoodPrompt(true);
                    }}
                    className="flex-1 btn-outline"
                    data-testid="skip-name-btn"
                  >
                    Skip
                  </button>
                  <button
                    type="submit"
                    className="flex-1 btn-champagne"
                    data-testid="continue-name-btn"
                  >
                    Continue
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mood Selection Modal */}
      <AnimatePresence>
        {showMoodPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="aura-card rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-xl font-serif font-medium text-text-light mb-2">
                What's the mood?
              </h3>
              <p className="text-text-muted mb-6">
                Select the vibe of your moment
              </p>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {MOODS.map((mood) => (
                  <button
                    key={mood.value}
                    onClick={() => handleMoodSelect(mood.value)}
                    className="p-4 rounded-xl border border-champagne/20 hover:border-champagne hover:bg-champagne/10 transition-all text-center"
                    data-testid={`mood-${mood.value}`}
                  >
                    <mood.Icon className="w-7 h-7 mx-auto mb-1 text-champagne" />
                    <span className="text-sm text-text-light">{mood.label}</span>
                  </button>
                ))}
              </div>
              <button
                onClick={() => handleMoodSelect(null)}
                className="w-full btn-outline"
                data-testid="skip-mood-btn"
              >
                Skip
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Caption Input Modal */}
      <AnimatePresence>
        {showCaptionPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="aura-card rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-xl font-serif font-medium text-text-light mb-2">
                Add a caption
              </h3>
              <p className="text-text-muted mb-4">
                Share a thought with your moment (optional)
              </p>
              <form onSubmit={handleCaptionSubmit}>
                <textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="What's the story behind this moment?"
                  className="aura-input mb-4 resize-none"
                  rows={3}
                  autoFocus
                  data-testid="caption-input"
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCaptionPrompt(false);
                      processFiles(pendingFiles, selectedMood, '');
                      setPendingFiles([]);
                    }}
                    className="flex-1 btn-outline"
                    data-testid="skip-caption-btn"
                  >
                    Skip
                  </button>
                  <button
                    type="submit"
                    className="flex-1 btn-champagne"
                    data-testid="upload-with-caption-btn"
                  >
                    Upload
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Leave a Note Modal (S37.6) */}
      <AnimatePresence>
        {showNoteModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
            <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
              className="aura-card rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-serif font-medium text-text-light mb-2">Leave a note</h3>
              <p className="text-text-muted text-sm mb-3">Up to 1000 characters</p>
              <textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={6} maxLength={1000}
                className="aura-input w-full resize-none" placeholder="What did this moment mean to you?" data-testid="note-text" />
              <div className="flex justify-between items-center mt-2 mb-3">
                <span className="text-xs text-text-muted">{noteText.length}/1000</span>
                {noteError && <span className="text-xs text-red-400">{noteError}</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShowNoteModal(false)} className="btn-outline flex-1">Cancel</button>
                <button onClick={submitNote} disabled={noteSubmitting} data-testid="submit-note-btn" className="btn-champagne flex-1">
                  {noteSubmitting ? 'Sending...' : 'Send note'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Voice Toast Recorder Modal */}
      <AnimatePresence>
        {showVoiceRecorder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              className="aura-card rounded-t-2xl sm:rounded-2xl w-full max-w-md p-6 text-center max-h-[90vh] overflow-y-auto"
            >
              <h3 className="text-xl font-serif font-medium text-text-light mb-2">
                Voice Toast
              </h3>
              <p className="text-text-muted mb-6">
                Record a 10-second message for the event
              </p>

              {/* Recording visualization */}
              <div className="mb-6">
                <div className={`w-24 h-24 rounded-full mx-auto flex items-center justify-center transition-all ${
                  isRecording 
                    ? 'bg-error animate-pulse' 
                    : audioBlob 
                      ? 'bg-gradient-to-br from-aura-purple to-aura-gold'
                      : 'bg-champagne/20'
                }`}>
                  {isRecording ? (
                    <MicOff className="w-10 h-10 text-white" />
                  ) : audioBlob ? (
                    <button onClick={playRecording} className="w-full h-full flex items-center justify-center">
                      {isPlaying ? (
                        <Pause className="w-10 h-10 text-white" />
                      ) : (
                        <Play className="w-10 h-10 text-white" />
                      )}
                    </button>
                  ) : (
                    <Mic className="w-10 h-10 text-champagne" />
                  )}
                </div>
                
                {/* Timer */}
                <p className="text-2xl font-mono text-text-light mt-4">
                  {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')} / 0:10
                </p>
              </div>

              {/* Audio playback element (hidden) */}
              {audioUrl && (
                <audio 
                  ref={audioPlayRef} 
                  src={audioUrl}
                  onEnded={() => setIsPlaying(false)}
                  className="hidden"
                />
              )}

              {/* Controls */}
              <div className="flex flex-col gap-3">
                {!audioBlob ? (
                  <>
                    {isRecording ? (
                      <button
                        onClick={stopRecording}
                        className="w-full bg-error text-white px-6 py-3 rounded-xl font-medium flex items-center justify-center gap-2"
                        data-testid="stop-recording-btn"
                      >
                        <Square className="w-5 h-5" />
                        Stop Recording
                      </button>
                    ) : (
                      <button
                        onClick={startRecording}
                        className="w-full btn-champagne flex items-center justify-center gap-2"
                        data-testid="start-recording-btn"
                      >
                        <Mic className="w-5 h-5" />
                        Start Recording
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      onClick={uploadVoiceToast}
                      className="w-full btn-champagne flex items-center justify-center gap-2"
                      data-testid="upload-voice-toast-btn"
                    >
                      <Upload className="w-5 h-5" />
                      Upload Voice Toast
                    </button>
                    <button
                      onClick={discardRecording}
                      className="w-full btn-outline flex items-center justify-center gap-2"
                      data-testid="discard-recording-btn"
                    >
                      <X className="w-5 h-5" />
                      Discard & Re-record
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    setShowVoiceRecorder(false);
                    discardRecording();
                  }}
                  className="text-text-muted hover:text-text-light transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PWA install banner */}
      <InstallBanner
        visitCount={visitCount}
        minVisits={2}
        brandName={brand?.company_name || 'Memouraa'}
        accentColor={brandAccent || '#D4AF37'}
      />
      {showReelEditor && (
        <ReelEditor
          auraId={aura?.id}
          photos={media}
          onClose={() => setShowReelEditor(false)}
          onUploaded={() => { setShowReelEditor(false); fetchAura(); }}
        />
      )}
    </div>
  );
}
